export interface FreeGamesBetData {
	chipSize: number;
	numChips: number;
	activePaylines: number;
}

export class FreeGamesInProgress {
	private _gamesPlayed: number;
	private _gamesRemaining: number;
	private _instanceID: string;
	private _offerID: string;
	private _sumWinnings: number;

	private _wagerChipSize: number;
	private _wagerNumChips: number;
	private _wagerActivePaylines: number;

	constructor(data: any) {
		this.parseFreeGamesInProgress(data);
	}

	public parseFreeGamesInProgress(data: any): void {
		if (!data) {
			return;
		}

		const freeGames: any = data.Response.FreeGames;

		if (freeGames && freeGames.CurrentGame) {
			this._gamesPlayed = Number.parseInt(freeGames.CurrentGame.attributes.gamesPlayed, 10);
			this._gamesRemaining = Number.parseInt(freeGames.CurrentGame.attributes.gamesRemaining, 10);
			this._sumWinnings = Number.parseInt(freeGames.CurrentGame.attributes.sumWinnings, 10);
			this._instanceID = freeGames.CurrentGame.attributes.instanceId;
			this._offerID = freeGames.CurrentGame.attributes.offerId;

			if (freeGames.LastRealWager) {
				this._wagerChipSize = Number.parseInt(freeGames.LastRealWager.attributes.chipSize, 10);
				this._wagerNumChips = Number.parseInt(freeGames.LastRealWager.attributes.numChips, 10);
				this._wagerActivePaylines = Number.parseInt(freeGames.LastRealWager.attributes.activePaylines, 10);
			}
		}
	}

	public setData(gamesPlayed: number, gamesRemaining: number, instanceID: string, offerID: string, sumWinnings: number, wagerChipSize: number, wagerNumChips: number, wagerActivePaylines: number): void {
		this._gamesPlayed = gamesPlayed;
		this._gamesRemaining = gamesRemaining;
		this._instanceID = instanceID;
		this._offerID = offerID;
		this._sumWinnings = sumWinnings;
		this._wagerChipSize = wagerChipSize;
		this._wagerNumChips = wagerNumChips;
		this._wagerActivePaylines = wagerActivePaylines;
	}

	public get gamesPlayed(): number {
		return this._gamesPlayed;
	}

	public get gamesRemaining(): number {
		return this._gamesRemaining;
	}

	public get instanceID(): string {
		return this._instanceID;
	}

	public get offerID(): string {
		return this._offerID;
	}

	public get sumWinnings(): number {
		return this._sumWinnings;
	}

	public get wagerChipSize(): number {
		return this._wagerChipSize;
	}

	public get wagerNumChips(): number {
		return this._wagerNumChips;
	}

	public get wagerActivePaylines(): number {
		return this._wagerActivePaylines;
	}

	public set gamesPlayed(value: number) {
		this._gamesPlayed = value;
	}

	public set gamesRemaining(value: number) {
		this._gamesRemaining = value;
	}

	public set instanceID(value: string) {
		this._instanceID = value;
	}

	public set offerID(value: string) {
		this._offerID = value;
	}

	public set sumWinnings(value: number) {
		this._sumWinnings = value;
	}

	public set wagerChipSize(value: number) {
		this._wagerChipSize = value;
	}

	public set wagerNumChips(value: number) {
		this._wagerNumChips = value;
	}

	public set wagerActivePaylines(value: number) {
		this._wagerActivePaylines = value;
	}

	public get lastRealWager(): FreeGamesBetData {
		return {chipSize: this.wagerChipSize, numChips: this.wagerNumChips, activePaylines: this.wagerActivePaylines};
	}
}

export class FreeGamesData {
	private _betData: FreeGamesBetData;
	private _creditType: number;
	private _endDate: string;
	private _gamesAwarded: number;
	private _gamesRemaining: number;
	private _instanceID: string;
	private _maxCost: number;
	private _name0: string;
	private _name1: string;
	private _offerID: string;
	private _sumWinnings: number;

	constructor(data: any) {
		if (data) {
			this.parseFreeGamesData(data);
		}
	}

	// TODO: remove
	public setData(betData: FreeGamesBetData, creditType: number, endDate: string, gamesAwarded: number,
				   gamesRemaining: number, instanceID: string, maxCost: number, name0: string, name1: string,
					offerID: string, sumWinnings: number): void {
		this._betData = betData;
		this._creditType = creditType;
		this._endDate = endDate;
		this._gamesAwarded = gamesAwarded;
		this._gamesRemaining = gamesRemaining;
		this._instanceID = instanceID;
		this._maxCost = maxCost;
		this._name0 = name0;
		this._name1 = name1;
		this._offerID = offerID;
		this._sumWinnings = sumWinnings;
	}

	public parseFreeGamesData(data: any): void {
		this._betData = {
			activePaylines: Number.parseInt(data.DefaultBet.attributes.activePaylines, 10),
			chipSize: Number.parseInt(data.DefaultBet.attributes.chipSize, 10),
			numChips: Number.parseInt(data.DefaultBet.attributes.numChips, 10),
		};
		this._creditType = Number.parseInt(data.attributes.creditType, 10);
		this._endDate = data.attributes.endDate;
		this._gamesAwarded = Number.parseInt(data.attributes.gamesAwarded, 10);
		this._gamesRemaining = this._gamesAwarded - Number.parseInt(data.attributes.gamesPlayed, 10);
		this._instanceID = data.attributes.instanceId;
		this._maxCost = Number.parseInt(data.attributes.maxCost, 10);
		this._name0 = data.attributes.name0;
		this._name1 = data.attributes.name1;
		this._offerID = data.attributes.offerId;
		this._sumWinnings = Number.parseInt(data.attributes.sumWinnings, 10);
	}

	public get betData(): FreeGamesBetData {
		return this._betData;
	}

	public get creditType(): number {
		return this._creditType;
	}

	public get endDate(): string {
		return this._endDate;
	}

	public get gamesAwarded(): number {
		return this._gamesAwarded;
	}

	public get gamesRemaining(): number {
		return this._gamesRemaining;
	}

	public get instanceID(): string {
		return this._instanceID;
	}

	public get maxCost(): number {
		return this._maxCost;
	}

	public get name0(): string {
		return this._name0;
	}

	public get name1(): string {
		return this._name1;
	}

	public get offerID(): string {
		return this._offerID;
	}

	public get sumWinnings(): number {
		return this._sumWinnings;
	}

	public set betData(value: FreeGamesBetData) {
		this._betData = value;
	}

	public set creditType(value: number) {
		this._creditType = value;
	}

	public set endDate(value: string) {
		this._endDate = value;
	}

	public set gamesAwarded(value: number) {
		this._gamesAwarded = value;
	}

	public set gamesRemaining(value: number) {
		this._gamesRemaining = value;
	}

	public set instanceID(value: string) {
		this._instanceID = value;
	}

	public set maxCost(value: number) {
		this._maxCost = value;
	}

	public set name0(value: string) {
		this._name0 = value;
	}

	public set name1(value: string) {
		this._name1 = value;
	}

	public set offerID(value: string) {
		this._offerID = value;
	}

	public set sumWinnings(value: number) {
		this._sumWinnings = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/integration/FreeGamesData.ts